import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaPlus, FaTimes } from "react-icons/fa";

import { mergeOptions } from "../utils/suiviClientsUtils";

export default function SuiviClientQuickCreateCard({
  column,
  productOptions = [],
  saving,
  onClose,
  onCreate,
}) {
  const inputRef = useRef(null);

  const [form, setForm] = useState({
    nom_asso: "",
    produit_asso: "",
    discipline_asso: "",
    date_relance_asso: "",
    detail_asso: "",
  });

  const productChoices = useMemo(() => {
    return mergeOptions(productOptions, form.produit_asso);
  }, [productOptions, form.produit_asso]);

  useEffect(() => {
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;

    root.style.overflow = "hidden";

    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 80);

    const onEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onEsc);

    return () => {
      root.style.overflow = previousOverflow;
      clearTimeout(timer);
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  const updateField = (name, value) => {
    setForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.nom_asso.trim()) return;

    onCreate?.({
      ...form,
      nom_asso: form.nom_asso.trim(),
    });
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/75 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-white/20"
      >
        <div
          className="h-2"
          style={{ backgroundColor: column?.color || "#facc15" }}
        />

        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              Ajouter une carte
            </h2>
            <p className="text-sm text-slate-500">
              Dans la colonne :{" "}
              <span className="font-bold">{column?.title || "-"}</span>
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition hover:bg-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <Field label="Nom association" required>
            <input
              ref={inputRef}
              value={form.nom_asso}
              onChange={(event) => updateField("nom_asso", event.target.value)}
              className="input-suivi-client"
              placeholder="Ex : Judo Club de..."
              required
            />
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Produit">
              <select
                value={form.produit_asso}
                onChange={(event) =>
                  updateField("produit_asso", event.target.value)
                }
                className="input-suivi-client"
              >
                <option value="">Choisir une application</option>
                {productChoices.map((product) => (
                  <option key={product} value={product}>
                    {product}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Discipline">
              <input
                value={form.discipline_asso}
                onChange={(event) =>
                  updateField("discipline_asso", event.target.value)
                }
                className="input-suivi-client"
                placeholder="Judo, Karaté, Mairie..."
              />
            </Field>
          </div>

          <Field label="Date de relance / fin abonnement">
            <input
              type="date"
              value={form.date_relance_asso}
              onChange={(event) =>
                updateField("date_relance_asso", event.target.value)
              }
              className="input-suivi-client"
            />
          </Field>

          <Field label="Note rapide">
            <textarea
              value={form.detail_asso}
              onChange={(event) =>
                updateField("detail_asso", event.target.value)
              }
              rows={3}
              className="input-suivi-client"
              placeholder="Première note, source, relance..."
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving || !form.nom_asso.trim()}
            className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaPlus />
            {saving ? "Création..." : "Créer"}
          </button>
        </div>

        <style>{`
          .input-suivi-client {
            width: 100%;
            border-radius: 0.75rem;
            border: 1px solid rgb(203 213 225);
            background: white;
            padding: 0.625rem 0.75rem;
            font-size: 0.875rem;
            outline: none;
          }

          .input-suivi-client:focus {
            border-color: rgb(139 92 246);
            box-shadow: 0 0 0 4px rgb(237 233 254);
          }
        `}</style>
      </form>
    </div>,
    document.body,
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}